import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, PAYMENT_RETURN_URL } from '../../config';
import LandingHeader from '../Shared/LandingHeader';
import LandingFooter from '../Shared/LandingFooter';
import './PricingPage.css';
import '../Shared/Modal.css';

const EMPTY_FORM = {
    school_name: '',
    contact_person: '',
    email: '',
    phone: '',
    city: '',
    student_count: '',
    message: ''
};

const PricingPage = () => {
    const { t } = useTranslation(['pricing', 'common', 'navigation']);
    const isLoggedIn = !!localStorage.getItem('userId');
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [cashfree, setCashfree] = useState(null);

    const pricingPlans = useMemo(() => [
        {
            key: 'free',
            name: t('pricing:plans.free.name'),
            price: t('pricing:plans.free.price'),
            features: t('pricing:plans.free.features', { returnObjects: true }),
            button: t('pricing:plans.free.button'),
            glow: "rgba(0, 212, 255, 0.3)"
        },
        {
            key: 'chapter',
            name: t('pricing:plans.chapter.name'),
            price: t('pricing:plans.chapter.price'),
            period: t('pricing:plans.chapter.period'),
            features: t('pricing:plans.chapter.features', { returnObjects: true }),
            button: t('pricing:plans.chapter.button'),
            recommended: true,
            glow: "rgba(157, 80, 187, 0.5)"
        },
        {
            key: 'premium',
            name: t('pricing:plans.premium.name'),
            price: t('pricing:plans.premium.price'),
            period: t('pricing:plans.premium.period'),
            features: t('pricing:plans.premium.features', { returnObjects: true }),
            button: t('pricing:plans.premium.button'),
            glow: "rgba(0, 255, 136, 0.3)"
        },
        {
            key: 'booster',
            name: t('pricing:plans.booster.name'),
            price: t('pricing:plans.booster.price'),
            period: t('pricing:plans.booster.period'),
            features: t('pricing:plans.booster.features', { returnObjects: true }),
            button: t('pricing:plans.booster.button'),
            glow: "rgba(255, 68, 68, 0.3)"
        },
        {
            key: 'partnership',
            name: t('pricing:plans.partnership.name'),
            hidePrice: true,
            contactSales: true,
            features: t('pricing:plans.partnership.features', { returnObjects: true }),
            button: t('pricing:plans.partnership.button'),
            glow: "rgba(255, 170, 0, 0.3)"
        }
    ], [t]);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.async = true;
        script.onload = () => {
            const cf = window.Cashfree({ mode: "production" });
            setCashfree(cf);
        };
        document.body.appendChild(script);
        return () => { document.body.removeChild(script); };
    }, []);

    const handlePayment = async (plan) => {
        if (!isLoggedIn) { navigate('/login'); return; }
        if (!cashfree) { alert(t('pricing:paymentGatewayLoading', 'Payment gateway is still loading.')); return; }

        try {
            const userId = localStorage.getItem('userId');
            const res = await fetch(`${API_BASE_URL}create_cashfree_order.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    user_id: userId,
                    amount: plan.price.replace('₹', ''),
                    plan_name: plan.name
                })
            });

            const data = await res.json();
            if (data.status === 'success' && data.payment_session_id) {
                cashfree.checkout({
                    paymentSessionId: data.payment_session_id,
                    returnUrl: `${PAYMENT_RETURN_URL}?order_id=${data.order_id}`,
                });
            } else {
                alert(t('pricing:paymentOrderError', 'Error creating order: ') + (data.message || "Unknown error"));
            }
        } catch (err) {
            console.error("Payment error:", err);
            alert(t('pricing:paymentGeneralError', 'An error occurred while initiating payment.'));
        }
    };

    const handleChange = (e) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitStatus(null);
        try {
            const res = await fetch(`${API_BASE_URL}school_inquiry.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (data.success) {
                setSubmitStatus('success');
                setForm(EMPTY_FORM);
            } else {
                setSubmitStatus('error');
            }
        } catch { setSubmitStatus('error'); }
        finally { setSubmitting(false); }
    };

    const closeModal = () => {
        setShowModal(false);
        setSubmitStatus(null);
        setForm(EMPTY_FORM);
    };

    return (
        <div className="pricing-page">
            <LandingHeader activePage="pricing" />

            <main style={{ paddingTop: '80px' }}>
                <section className="pricing-hero">
                    <div className="container">
                        <h1 className="hero-title">
                            {t('pricing:hero.title')} <span className="gradient-text">{t('pricing:hero.titleHighlight')}</span>
                        </h1>
                        <p className="hero-subtext">{t('pricing:hero.subtext')}</p>
                    </div>
                </section>

                <section className="pricing-table-section">
                    <div className="container">
                        <div className="pricing-grid">
                            {pricingPlans.map((plan, index) => (
                                <div key={index} className={`pricing-card ${plan.recommended ? 'recommended' : ''}`} style={{ '--card-glow': plan.glow }}>
                                    {plan.recommended && <div className="recommend-badge">{t('pricing:plans.recommended')}</div>}
                                    <h3>{plan.name}</h3>
                                    <div className="price-tag">
                                        {plan.hidePrice ? (
                                            <span className="pricing-amount">{t('pricing:plans.customPricing')}</span>
                                        ) : (
                                            <>
                                                <span className="pricing-amount">{plan.price}</span>
                                                {plan.period && <span className="period">{plan.period}</span>}
                                            </>
                                        )}
                                    </div>
                                    <ul className="plan-features">
                                        {Array.isArray(plan.features) && plan.features.map((feature, i) => (
                                            <li key={i}><span>✓</span> {feature}</li>
                                        ))}
                                    </ul>
                                    {plan.contactSales ? (
                                        <button className="btn btn-secondary" onClick={() => setShowModal(true)}>{plan.button}</button>
                                    ) : plan.price === "₹0" ? (
                                        <Link to="/register" className="btn btn-secondary">{plan.button}</Link>
                                    ) : (
                                        <button
                                            className={`btn ${plan.recommended ? 'btn-primary' : 'btn-secondary'}`}
                                            onClick={() => handlePayment(plan)}
                                        >
                                            {plan.button}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="stats-section">
                    <div className="container">
                        <h2 className="section-title">{t('pricing:stats.title')} <span className="gradient-text">{t('pricing:stats.titleHighlight')}</span></h2>
                        <div className="stats-container">
                            <div className="stats-content">
                                <h3>{t('pricing:stats.improvementTitle')}</h3>
                                <div className="progress-stat">
                                    <div className="stat-label">{t('pricing:stats.before')}</div>
                                    <div className="stat-bar-track"><div className="stat-bar-fill before" style={{ width: '65%' }}></div></div>
                                </div>
                                <div className="progress-stat">
                                    <div className="stat-label">{t('pricing:stats.after')}</div>
                                    <div className="stat-bar-track"><div className="stat-bar-fill after" style={{ width: '92%' }}></div></div>
                                </div>
                                <p>{t('pricing:stats.text')}</p>
                            </div>
                            <div className="stats-visual">
                                <div className="avatar-grid">
                                    {[1, 2, 3].map(i => <div key={i} className="stat-avatar-placeholder">🎓</div>)}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {!isLoggedIn && (
                    <section className="cta-section container">
                        <h2 style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>{t('pricing:cta.title')} <span className="gradient-text">{t('pricing:cta.titleHighlight')}</span></h2>
                        <p style={{ marginBottom: '2.5rem', color: 'var(--text-gray)' }}>{t('pricing:cta.text')}</p>
                        <Link to="/register" className="btn btn-primary" style={{ fontSize: '1.2rem' }}>{t('pricing:cta.button')}</Link>
                    </section>
                )}
            </main>

            <LandingFooter />

            {showModal && (
                <div className="sp-modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
                    <div className="sp-modal">
                        <button className="sp-modal-close" onClick={closeModal}>✕</button>
                        <div className="sp-modal-header">
                            <span className="sp-modal-icon">🏫</span>
                            <h2>{t('pricing:modal.title')}</h2>
                            <p>{t('pricing:modal.subtext')}</p>
                        </div>

                        {submitStatus === 'success' ? (
                            <div className="sp-success">
                                <div className="sp-success-icon">✅</div>
                                <h3>{t('pricing:modal.success.title')}</h3>
                                <p>{t('pricing:modal.success.text')}</p>
                                <button className="btn btn-primary" onClick={closeModal}>{t('pricing:modal.success.close')}</button>
                            </div>
                        ) : (
                            <form className="sp-form" onSubmit={handleSubmit}>
                                <div className="sp-form-row">
                                    <div className="sp-field">
                                        <label>{t('pricing:modal.form.schoolName')}</label>
                                        <input name="school_name" value={form.school_name} onChange={handleChange} placeholder={t('pricing:modal.form.schoolNamePlaceholder')} required />
                                    </div>
                                    <div className="sp-field">
                                        <label>{t('pricing:modal.form.contactPerson')}</label>
                                        <input name="contact_person" value={form.contact_person} onChange={handleChange} placeholder={t('pricing:modal.form.contactPersonPlaceholder')} required />
                                    </div>
                                </div>
                                <div className="sp-form-row">
                                    <div className="sp-field">
                                        <label>{t('pricing:modal.form.email')}</label>
                                        <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="admin@school.edu" required />
                                    </div>
                                    <div className="sp-field">
                                        <label>{t('pricing:modal.form.phone')}</label>
                                        <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" required />
                                    </div>
                                </div>
                                <div className="sp-form-row">
                                    <div className="sp-field">
                                        <label>{t('pricing:modal.form.city')}</label>
                                        <input name="city" value={form.city} onChange={handleChange} placeholder="City" required />
                                    </div>
                                    <div className="sp-field">
                                        <label>{t('pricing:modal.form.studentCount')}</label>
                                        <select name="student_count" value={form.student_count} onChange={handleChange}>
                                            <option value="">{t('pricing:modal.form.studentCountPlaceholder')}</option>
                                            {t('pricing:modal.form.studentCountOptions', { returnObjects: true }).map((opt, i) => (
                                                <option key={i} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="sp-field">
                                    <label>{t('pricing:modal.form.message')}</label>
                                    <textarea name="message" value={form.message} onChange={handleChange} rows="3" placeholder={t('pricing:modal.form.messagePlaceholder')}></textarea>
                                </div>

                                {submitStatus === 'error' && <p className="sp-error">{t('pricing:modal.error')}</p>}

                                <button type="submit" className="btn btn-primary sp-submit" disabled={submitting}>
                                    {submitting ? t('pricing:modal.form.submitting') : t('pricing:modal.form.submit')}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PricingPage;
